import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Filter, Download, Camera, ChevronRight, FileText, X, Printer, CheckSquare, Square } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLicense } from "@/context/LicenseContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
const Dashboard = () => {
  const navigate = useNavigate();
  const { isReadOnly } = useLicense();
  const [tires, setTires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [marcaFilter, setMarcaFilter] = useState("all");
  const [magazzinoFilter, setMagazzinoFilter] = useState("all");
  const [statoFilter, setStatoFilter] = useState("all");
  const [marche, setMarche] = useState([]);
  const [magazzini, setMagazzini] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedTires, setSelectedTires] = useState([]);
  const [selectMode, setSelectMode] = useState(false);

  const fetchTires = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (marcaFilter && marcaFilter !== "all") params.append("marca", marcaFilter);
      if (magazzinoFilter && magazzinoFilter !== "all") params.append("magazzino", magazzinoFilter);
      if (statoFilter && statoFilter !== "all") params.append("stato", statoFilter);

      const response = await api.get(`/tires?${params.toString()}`);
      setTires(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei pneumatici");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [marcheRes, magazziniRes] = await Promise.all([
        api.get("/filters/marche"),
        api.get("/filters/magazzini"),
      ]);
      setMarche(marcheRes.data);
      setMagazzini(magazziniRes.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  useEffect(() => {
    fetchTires();
    fetchFilters();
  }, [search, marcaFilter, magazzinoFilter, statoFilter]);

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (marcaFilter && marcaFilter !== "all") params.append("marca", marcaFilter);
      if (magazzinoFilter && magazzinoFilter !== "all") params.append("magazzino", magazzinoFilter);
      if (statoFilter && statoFilter !== "all") params.append("stato", statoFilter);

      const response = await api.get(`/export/${format}?${params.toString()}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pneumatici.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Export ${format.toUpperCase()} completato`);
    } catch (error) {
      toast.error(`Errore nell'export ${format.toUpperCase()}`);
    }
  };

  const handlePrintLabels = async () => {
    if (selectedTires.length === 0) {
      toast.error("Seleziona almeno un pneumatico");
      return;
    }

    try {
      const codes = selectedTires.join(",");
      const response = await api.get(`/export/labels?codes=${codes}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `etichette.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${selectedTires.length} etichette scaricate`);
      setSelectMode(false);
      setSelectedTires([]);
    } catch (error) {
      toast.error("Errore nel download delle etichette");
    }
  };

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedTires([]);
  };

  const toggleTireSelection = (code) => {
    setSelectedTires(prev => 
      prev.includes(code) 
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );
  };

  const selectAll = () => {
    if (selectedTires.length === tires.length) {
      setSelectedTires([]);
    } else {
      setSelectedTires(tires.map(t => t.code));
    }
  };

  const clearFilters = () => {
    setSearch("");
    setMarcaFilter("all");
    setMagazzinoFilter("all");
    setStatoFilter("all");
  };

  const hasActiveFilters = search || (marcaFilter && marcaFilter !== "all") || (magazzinoFilter && magazzinoFilter !== "all") || (statoFilter && statoFilter !== "all");

  const displayedTires = tires.filter((tire) => {
    if (statoFilter && statoFilter !== "all" && tire.stato !== statoFilter) return false;
    return true;
  });

  return (
    <div data-testid="dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            <span className="text-primary">Tyre</span>Track
          </h1>
          <p className="text-muted-foreground mt-1">
            {tires.reduce((totale, tire) => totale + tire.numero_pneumatici, 0)} pneumatici in magazzino
          </p>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto">
          {selectMode ? (
            <>
              <Button
                data-testid="cancel-select-btn"
                variant="secondary"
                onClick={toggleSelectMode}
                className="h-9 sm:h-12 px-2.5 sm:px-4 text-xs sm:text-sm font-semibold flex-1 sm:flex-initial justify-center"
              >
                <X className="w-4 h-4 sm:w-[18px] sm:h-[18px] mr-1 sm:mr-2" />
                Annulla
              </Button>
              <Button
                data-testid="select-all-btn"
                variant="secondary"
                onClick={selectAll}
                className="h-9 sm:h-12 px-2.5 sm:px-4 text-xs sm:text-sm font-semibold flex-1 sm:flex-initial justify-center"
              >
                {selectedTires.length === tires.length ? (
                  <Square className="w-4 h-4 sm:w-[18px] sm:h-[18px] mr-1 sm:mr-2" />
                ) : (
                  <CheckSquare className="w-4 h-4 sm:w-[18px] sm:h-[18px] mr-1 sm:mr-2" />
                )}
                {selectedTires.length === tires.length ? "Deseleziona" : "Seleziona tutti"}
              </Button>
              <Button
                data-testid="print-selected-btn"
                onClick={handlePrintLabels}
                disabled={selectedTires.length === 0}
                className="h-9 sm:h-12 px-2.5 sm:px-6 text-xs sm:text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 btn-glow flex-1 sm:flex-initial justify-center"
              >
                <Printer className="w-4 h-4 sm:w-[18px] sm:h-[18px] mr-1 sm:mr-2" />
                Stampa ({selectedTires.length})
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="print-labels-btn"
                variant="secondary"
                onClick={toggleSelectMode}
                className="h-9 sm:h-12 px-2.5 sm:px-4 text-xs sm:text-sm font-semibold flex-1 sm:flex-initial justify-center"
              >
                <Printer className="w-4 h-4 sm:w-[18px] sm:h-[18px] mr-1 sm:mr-2" />
                Etichette
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="export-btn"
                    variant="secondary"
                    className="h-9 sm:h-12 px-2.5 sm:px-4 text-xs sm:text-sm font-semibold flex-1 sm:flex-initial justify-center"
                  >
                    <Download className="w-4 h-4 sm:w-[18px] sm:h-[18px] mr-1 sm:mr-2" />
                    Esporta
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    data-testid="export-csv"
                    onClick={() => handleExport("csv")}
                  >
                    <FileText size={16} className="mr-2" />
                    Esporta CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="export-pdf"
                    onClick={() => handleExport("pdf")}
                  >
                    <FileText size={16} className="mr-2" />
                    Esporta PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                data-testid="new-tyre-btn"
                onClick={() => navigate("/capture")}
                disabled={isReadOnly}
                className="h-9 sm:h-12 px-3 sm:px-6 text-xs sm:text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 btn-glow flex-1 sm:flex-initial justify-center"
              >
                <Camera className="w-4 h-4 sm:w-[18px] sm:h-[18px] mr-1 sm:mr-2" />
                Nuovo
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              size={18}
            />
            <Input
              data-testid="search-input"
              placeholder="Cerca per codice, misura o marca..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12 bg-input/50 border-input font-mono text-sm"
            />
          </div>
          <Button
            data-testid="filter-toggle-btn"
            variant="secondary"
            onClick={() => setShowFilters(!showFilters)}
            className={`h-12 px-4 ${showFilters ? "bg-primary text-primary-foreground" : ""}`}
          >
            <Filter size={18} />
          </Button>
        </div>

        {showFilters && (
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 p-4 bg-card border border-border rounded-md card-animate">
            <Select value={statoFilter} onValueChange={setStatoFilter}>
              <SelectTrigger data-testid="stato-filter" className="h-12 bg-input/50 flex-1">
                <SelectValue placeholder="Filtra per stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le <span className="font-bold underline">Tipologie</span></SelectItem>
                <SelectItem value="Nuovo">Nuovo</SelectItem>
                <SelectItem value="Usato">Usato</SelectItem>
                <SelectItem value="Deposito">Deposito</SelectItem>
                <SelectItem value="Registro PFU">Registro PFU</SelectItem>
              </SelectContent>
            </Select>

            <Select value={marcaFilter} onValueChange={setMarcaFilter}>
              <SelectTrigger data-testid="marca-filter" className="h-12 bg-input/50 flex-1">
                <SelectValue placeholder="Filtra per marca" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le <span className="font-bold underline">Marche</span></SelectItem>
                {marche.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={magazzinoFilter} onValueChange={setMagazzinoFilter}>
              <SelectTrigger data-testid="magazzino-filter" className="h-12 bg-input/50 flex-1">
                <SelectValue placeholder="Filtra per magazzino" />
              </SelectTrigger>
              <SelectContent>
              <SelectItem value="all">Tutti i <span className="font-bold underline">Magazzini</span></SelectItem>
                {magazzini.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                data-testid="clear-filters-btn"
                variant="ghost"
                onClick={clearFilters}
                className="h-12 shrink-0 whitespace-nowrap"
              >
                <X size={16} className="mr-2" />
                Pulisci filtri
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Tire List */}
      {loading ? (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="bg-card border-border animate-pulse">
              <CardContent className="p-4 h-24" />
            </Card>
          ))}
        </div>
      ) : displayedTires.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <Camera size={28} className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Nessun pneumatico trovato</h3>
            <p className="text-muted-foreground mb-4">
              {hasActiveFilters
                ? "Prova a modificare i filtri di ricerca"
                : "Inizia aggiungendo il tuo primo pneumatico"}
            </p>
            {!hasActiveFilters && (
              <Button
                data-testid="empty-state-new-btn"
                onClick={() => navigate("/capture")}
                disabled={isReadOnly}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Camera size={18} className="mr-2" />
                Aggiungi Pneumatico
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {displayedTires.map((tire, index) => (
            <Card
              key={tire.id}
              data-testid={`tyre-card-${tire.code}`}
              onClick={() => selectMode ? toggleTireSelection(tire.code) : navigate(`/tyre/${tire.code}`)}
              className={`bg-card border-border cursor-pointer card-hover card-animate ${
                selectedTires.includes(tire.code) ? "border-primary bg-primary/5" : ""
              }`}
              style={{ animationDelay: `${Math.min(index, 5) * 0.05}s` }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  {selectMode && (
                    <div className="mr-4">
                      {selectedTires.includes(tire.code) ? (
                        <CheckSquare className="text-primary" size={24} />
                      ) : (
                        <Square className="text-muted-foreground" size={24} />
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono font-semibold text-primary text-lg tracking-wider">
                        {tire.code}
                      </span>
                      {tire.has_photo && (
                        <span className="text-xs bg-secondary px-2 py-1 rounded text-muted-foreground">
                          Foto
                        </span>
                      )}
                      {tire.stato && (
                        <Badge
                          variant="secondary"
                          className={
                            tire.stato === "Nuovo"
                              ? "bg-green-500/20 text-green-400"
                              : tire.stato === "Usato"
                              ? "bg-yellow-500/20 text-yellow-400"
                              : tire.stato === "Deposito"
                              ? "bg-blue-500/20 text-blue-400"
                              : "bg-purple-500/20 text-purple-400"
                          }
                        >
                          {tire.stato}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm mb-2 line-clamp-1">
                      <span className="text-muted-foreground">Nome: </span>
                      <span className="font-medium text-foreground">
                        {tire.nome_prodotto || (tire.marca + " " + tire.misura) || "-"}
                      </span>
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs sm:text-sm">
                      <div>
                        <span className="text-muted-foreground">Misura: </span>
                        <span className="font-medium">{tire.misura}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Marca: </span>
                        <span className="font-medium">{tire.marca}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Qty: </span>
                        <span className="font-medium">{tire.numero_pneumatici}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Magazzino: </span>
                        <span className="font-medium">{tire.magazzino}</span>
                      </div>
                    </div>
                  </div>
                  {!selectMode && (
                    <ChevronRight className="text-muted-foreground ml-4" size={20} />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
